// The route error boundary.
//
// The error this exists for: after a deploy, every JS chunk gets a new content hash. A tab that has
// been open across the deploy — or a PWA holding an old cached index.html — still asks for the old
// filename, which no longer exists, and React Router shows its raw "Unexpected Application Error"
// developer screen to a member of lab staff mid-shift.
//
// A stale chunk is not really an error: the app is simply out of date. So we reload once, which
// fetches fresh HTML and therefore the current hashes. The reload is guarded by a timestamp so a
// genuinely broken deploy degrades into a readable message instead of a reload loop.
import { isRouteErrorResponse, Link, useRouteError } from 'react-router-dom';
import { isStaleBuildError, reloadOntoCurrentBuild, reloadedRecently } from '../lib/staleBuild.ts';
import { AlignovaMark } from '../ui/AlignovaLogo.tsx';
import { IconArrowLeft } from '../ui/icons.tsx';

function messageOf(error: unknown): string {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
  if (error instanceof Error) return error.message;
  return typeof error === 'string' ? error : 'Something went wrong.';
}

export function AppError() {
  const error = useRouteError();
  const message = messageOf(error);
  const isStale = isStaleBuildError(message);

  // Self-heal: a new build is live and this tab is holding the old one.
  if (isStale && !reloadedRecently()) {
    reloadOntoCurrentBuild();
    return (
      <div className="grid min-h-full place-items-center p-6 text-center">
        <div>
          <AlignovaMark className="mx-auto mb-3 h-10 w-10 animate-pulse" style={{ color: 'var(--c-primary)' }} />
          <p className="text-sm text-muted">Updating to the latest version…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid min-h-full place-items-center p-6">
      <div className="w-full max-w-md text-center">
        <span className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-xl bg-surface-2 text-muted">
          <AlignovaMark className="h-9 w-9" />
        </span>
        <h1 className="text-lg font-semibold text-ink">
          {isStale ? 'Reloading didn’t help' : 'Something went wrong'}
        </h1>
        <p className="mx-auto mt-1 max-w-[42ch] text-sm text-muted">
          {isStale
            ? 'This device is holding an old copy of the app. Close the tab (or the installed app) and open it again - if it keeps happening, tell the lab.'
            : 'The page could not be displayed. Your work is not lost - go back and try again.'}
        </p>
        <p className="mx-auto mt-3 max-w-[46ch] break-words rounded-md bg-surface-2 px-3 py-2 text-left font-mono text-[11px] text-muted">
          {message}
        </p>
        <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-center">
          <button
            onClick={() => window.location.reload()}
            className="tap inline-flex items-center justify-center gap-2 rounded-md border border-line px-4 py-2.5 text-sm font-medium text-ink"
          >
            <IconArrowLeft size={18} />
            Reload
          </button>
          <Link
            to="/"
            className="tap inline-flex items-center justify-center rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-fg"
          >
            Go to the dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
